import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  ArrowRight, Check, ChevronDown, Shield, Sparkles, X, Mail, Building2, Users,
  LayoutDashboard, ClipboardCheck, Rocket, Send, Loader2, FileBarChart, MessageSquare,
  Server, Layers, TrendingUp
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { PRICING_CONFIG } from '@/lib/pricingConfig';
import { detectDefaultCurrency, setStoredCurrency } from '@/lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOT = '/platform';

// Why adopt (not build) - core value proposition for agencies
const valueProps = [
  {
    icon: Server,
    title: "Don't build it, adopt it",
    body: 'Skip 12 to 18 months of product development and six-figure engineering cost. Launch a complete client platform under your agency this week.'
  },
  {
    icon: Users,
    title: 'Onboard law firms in minutes',
    body: 'A guided intake collects everything you need, from firm details and services to locations and brand assets, then provisions a ready dashboard instantly.'
  },
  {
    icon: Layers,
    title: 'Run every service in one place',
    body: 'SEO, content, backlinks, reporting, approvals and communication live in a single dashboard your clients actually log into.'
  },
  {
    icon: Shield,
    title: 'Look enterprise-grade',
    body: 'Give every client a structured, transparent, professional experience that makes your agency impossible to replace.'
  }
];

// Platform tour - real product screenshots
const tour = [
  {
    img: `${SHOT}/onboarding.webp`,
    tag: 'Client Onboarding',
    title: 'Onboard new law-firm clients with a guided intake',
    body: 'A clean, three-step onboarding wizard captures firm details, services, target clients, country and brand assets, then spins up a personalised dashboard automatically.',
    points: ['Firm profile & logo', 'Services & practice areas', 'Country-aware currency & reporting']
  },
  {
    img: `${SHOT}/overview.webp`,
    tag: 'Unified Dashboard',
    title: 'One dashboard your clients log into every day',
    body: 'Give each firm a live command centre with AI visibility, organic performance, recent activity and everything your team is delivering, all in real time.',
    points: ['Real-time activity feed', 'AI + organic performance', 'Per-client, fully isolated data']
  },
  {
    img: `${SHOT}/position-tracking.webp`,
    tag: 'Rank Tracking',
    title: 'Search & AI visibility your clients can actually see',
    body: 'Upload rankings and AI-engine visibility and present movement clearly. No more black-box reporting or "trust us" conversations.',
    points: ['Search engine rankings', 'AI engine visibility (ChatGPT, Perplexity, Gemini)', 'Clear movement with +/- changes']
  },
  {
    img: `${SHOT}/traffic-analytics.webp`,
    tag: 'Analytics',
    title: 'Google Analytics & Search Console, client-ready',
    body: 'Connected traffic and conversion data presented in plain language, giving your clients the insight they want without the spreadsheet dump.',
    points: ['Sessions, users & conversions', 'Top landing pages', 'Connected GA4 & GSC']
  },
  {
    img: `${SHOT}/content.webp`,
    tag: 'Approvals',
    title: 'Content & approval workflows built in',
    body: 'Submit blogs, pages, metadata and GMB updates for review. Clients approve or request changes right inside the dashboard, and every decision is logged.',
    points: ['Blog & page approvals', 'Request-changes workflow', 'Full audit trail']
  },
  {
    img: `${SHOT}/reporting.webp`,
    tag: 'Reporting',
    title: 'Centralised, branded monthly reporting',
    body: 'Pull the whole month into a single, professional report your clients trust, with no manual assembly across five different tools.',
    points: ['Month-over-month summaries', 'Deliverables in one view', 'Export & share']
  },
  {
    img: `${SHOT}/chat.webp`,
    tag: 'Communication',
    title: 'Keep every client conversation in one thread',
    body: 'A dedicated chat connects each firm to their account manager, so context stays in the platform instead of scattered across inboxes.',
    points: ['Per-client messaging', 'Account-manager assignment', 'Email notifications on demand']
  }
];

// Additional capabilities shown as a visual grid
const extraServices = [
  { img: `${SHOT}/social.webp`, title: 'Social Media' },
  { img: `${SHOT}/paid-ads.webp`, title: 'Paid Ads' },
  { img: `${SHOT}/ai-video.webp`, title: 'AI Video' },
  { img: `${SHOT}/website-creation.webp`, title: 'Website Creation' },
  { img: `${SHOT}/keywords.webp`, title: 'Keywords & Queries' },
  { img: `${SHOT}/legal-translations.webp`, title: 'Legal Translations' }
];

// Capability summary
const capabilities = [
  { icon: Users, label: 'Client onboarding' },
  { icon: LayoutDashboard, label: 'Live client dashboards' },
  { icon: TrendingUp, label: 'Rank & AI visibility tracking' },
  { icon: FileBarChart, label: 'Centralised reporting' },
  { icon: ClipboardCheck, label: 'Content & approval workflows' },
  { icon: MessageSquare, label: 'Client communication' },
  { icon: Shield, label: 'Access & permissions' },
  { icon: Layers, label: 'Add-on services engine' }
];

// How an agency rolls it out
const agencySteps = [
  { number: '01', title: 'Adopt the platform', description: 'Add Law Agency 360 as the technology layer behind your agency, with no internal build and no engineering team required.' },
  { number: '02', title: 'Onboard your clients', description: 'Invite each law firm through the guided intake. Dashboards, reporting and workflows are provisioned automatically.' },
  { number: '03', title: 'Deliver & manage', description: 'Run SEO, content, ads, reporting and approvals from one place while clients watch the work happen live.' },
  { number: '04', title: 'Scale with confidence', description: 'Add more firms without adding more tools, spreadsheets or chaos. The client experience stays consistent every time.' }
];

// FAQ data (agency-focused)
const faqs = [
  { q: 'What exactly is Law Agency 360 for an agency?', a: 'It is a complete, ready-built client delivery and management platform. Instead of building your own dashboards, reporting, onboarding and approval tools, you adopt ours and run your law-firm clients inside it, adding a full technology and client-experience layer to your business overnight.' },
  { q: 'Can I use it alongside the services I already deliver?', a: 'Yes. The platform is the delivery and visibility layer on top of your existing work across SEO, content, backlinks, ads, social, websites and more. You keep doing what you do best, and your clients get a structured, transparent experience around it.' },
  { q: 'How do my clients get onboarded?', a: 'Each firm goes through a short guided intake that collects their details, services, locations and brand assets, then automatically provisions a personalised dashboard. Onboarding that used to take days happens in minutes.' },
  { q: 'Do my clients need technical knowledge?', a: 'No. Everything is presented in plain language with clear visuals across rankings, reporting, approvals and messaging, so firms of any size can use it confidently from day one.' },
  { q: 'How is pricing structured?', a: 'Your clients can be placed on simple monthly plans (shown below) with no long-term contracts. Want to discuss adopting the platform across your whole client base? Request a demo and we will tailor it to your agency.' }
];

// Pricing plans
const currencies = {
  GBP: { code: 'GBP', symbol: '£', label: 'GBP' },
  EUR: { code: 'EUR', symbol: '€', label: 'EUR' },
  USD: { code: 'USD', symbol: '$', label: 'USD' }
};

const plans = [
  {
    id: 'taster', title: 'Taster', tagline: 'Best for: Solo practitioners just getting started',
    monthly: { GBP: PRICING_CONFIG.taster.monthly.gbp, EUR: PRICING_CONFIG.taster.monthly.eur, USD: PRICING_CONFIG.taster.monthly.usd },
    yearly:  { GBP: PRICING_CONFIG.taster.yearly.gbp,  EUR: PRICING_CONFIG.taster.yearly.eur,  USD: PRICING_CONFIG.taster.yearly.usd },
    features: ['Live dashboard access', 'Up to 8 keywords tracked', '1 blog per month', '100 backlinks per month', 'Monthly ranking report', 'Google Analytics and Search Console Integration', 'Email support only'],
    excluded: ['No account manager', 'No AI visibility tracking'], highlighted: false
  },
  {
    id: 'growth', title: 'Growth', tagline: 'Best for: Boutique firms scaling up',
    monthly: { GBP: PRICING_CONFIG.growth.monthly.gbp, EUR: PRICING_CONFIG.growth.monthly.eur, USD: PRICING_CONFIG.growth.monthly.usd },
    yearly:  { GBP: PRICING_CONFIG.growth.yearly.gbp,  EUR: PRICING_CONFIG.growth.yearly.eur,  USD: PRICING_CONFIG.growth.yearly.usd },
    features: ['Everything in Taster', 'Up to 20 keywords tracked', '3 blogs per month', '200 backlinks per month', 'AI search visibility tracking (ChatGPT, Perplexity, Gemini)', 'Dedicated account manager', 'Client chat with account manager', 'Quarterly review call', 'Priority email support'],
    excluded: [], highlighted: true, badge: 'Most Popular'
  },
  {
    id: 'authority', title: 'Authority', tagline: 'Best for: Established firms dominating their market',
    monthly: { GBP: PRICING_CONFIG.authority.monthly.gbp, EUR: PRICING_CONFIG.authority.monthly.eur, USD: PRICING_CONFIG.authority.monthly.usd },
    yearly:  { GBP: PRICING_CONFIG.authority.yearly.gbp,  EUR: PRICING_CONFIG.authority.yearly.eur,  USD: PRICING_CONFIG.authority.yearly.usd },
    features: ['Everything in Growth', '40 keywords tracked', '8 blogs per month', '400 backlinks per month', 'AI search visibility tracking (ChatGPT, Perplexity, Gemini)', 'Full LLM presence tracking', 'Priority dedicated account manager', 'Monthly review call', 'Custom reporting'],
    excluded: [], highlighted: false
  }
];

const formatPrice = (amount) => amount.toLocaleString('en-US');

function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold: 0.1, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={`transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left group"
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-lg font-medium text-slate-900 group-hover:text-violet-600 transition-colors pr-8">{question}</span>
        <ChevronDown className={`w-5 h-5 text-violet-600 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-6' : 'max-h-0'}`}>
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

// Reusable browser-chrome frame for screenshots
function ShotFrame({ src, alt, className = '' }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-xl shadow-slate-300/40 ${className}`}>
      <div className="flex items-center gap-1.5 px-4 h-9 bg-slate-100 border-b border-slate-200">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="w-full block" />
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', agency: '', phone: '', clients: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full h-12 px-4 rounded-xl bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus('sending');
    try {
      await axios.post(`${API}/agency-enquiry`, form);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="bg-white border border-emerald-300 rounded-3xl p-10 text-center shadow-sm" data-testid="enquiry-success">
        <div className="w-14 h-14 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
          <Check className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Thanks, we'll be in touch.</h3>
        <p className="text-slate-600">Your enquiry has landed with our team. Expect a reply at <span className="text-slate-900 font-medium">{form.email}</span> shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-8 md:p-10 space-y-5 shadow-sm" data-testid="agency-enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Your name *</label>
          <input required value={form.name} onChange={set('name')} data-testid="enquiry-name" className={inputCls} placeholder="Jane Smith" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Work email *</label>
          <input required type="email" value={form.email} onChange={set('email')} data-testid="enquiry-email" className={inputCls} placeholder="jane@youragency.com" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Agency name</label>
          <input value={form.agency} onChange={set('agency')} data-testid="enquiry-agency" className={inputCls} placeholder="Your Agency Ltd" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Phone (optional)</label>
          <input value={form.phone} onChange={set('phone')} data-testid="enquiry-phone" className={inputCls} placeholder="+44 ..." />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">How many law-firm clients do you manage?</label>
        <input value={form.clients} onChange={set('clients')} data-testid="enquiry-clients" className={inputCls} placeholder="e.g. 8 firms" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Anything you'd like us to know?</label>
        <textarea rows={4} value={form.message} onChange={set('message')} data-testid="enquiry-message"
          className="w-full px-4 py-3 rounded-xl bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all resize-none"
          placeholder="Tell us about your agency and what you're looking for." />
      </div>
      {status === 'error' && (
        <p className="text-rose-600 text-sm" data-testid="enquiry-error">Something went wrong. Please try again or email support@lawagency360.com.</p>
      )}
      <button type="submit" disabled={status === 'sending'} data-testid="enquiry-submit"
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold text-lg transition-all hover:shadow-2xl hover:shadow-violet-500/30 flex items-center justify-center gap-2 disabled:opacity-60">
        {status === 'sending' ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>) : (<><Send className="w-5 h-5" /> Request a demo</>)}
      </button>
    </form>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [currency, setCurrencyState] = useState(() => detectDefaultCurrency().toUpperCase());
  const setCurrency = (code) => { setCurrencyState(code); setStoredCurrency(code); };
  const [billing, setBilling] = useState('monthly');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden">
      {/* Background gradient overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#ffffff_0%,_#f1f5f9_55%)]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-violet-300/20 rounded-full blur-[120px]"></div>
      </div>

      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-xl border-b border-slate-200 shadow-sm' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-2">
              <BrandLogo size={40} />
              <span className="text-xl font-bold tracking-tight text-slate-900">Law Agency 360</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollTo('platform')} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Platform</button>
              <button onClick={() => scrollTo('how-it-works')} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">How it Works</button>
              <button onClick={() => scrollTo('pricing')} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Pricing</button>
              <button onClick={() => navigate('/blog')} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Blog</button>
              <button onClick={() => navigate('/about')} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium" data-testid="nav-about-link">About</button>
            </div>

            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/auth?mode=signin')} className="hidden sm:block text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">Sign In</button>
              <button
                onClick={() => scrollTo('contact')}
                className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:shadow-violet-500/25"
                data-testid="nav-request-demo-btn"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-36 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 border border-violet-200 rounded-full mb-7" data-testid="hero-badge">
              <Building2 className="w-4 h-4 text-violet-600" />
              <span className="text-violet-700 text-sm font-medium">For digital marketing agencies serving law firms</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-slate-900" data-testid="hero-headline">
              The client platform your agency
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">didn't have to build.</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto" data-testid="hero-subcopy">
              Law Agency 360 is a complete, ready-built delivery and management system for agencies that work with law firms.
              Add a full technology and client-experience layer to your business, including onboarding, dashboards, reporting,
              approvals and communication, without building any of it from scratch.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => scrollTo('contact')} data-testid="hero-cta-primary"
                className="group px-8 py-4 bg-violet-600 hover:bg-violet-500 text-white rounded-2xl font-semibold text-lg transition-all hover:shadow-2xl hover:shadow-violet-500/30 flex items-center gap-2">
                Request a demo <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => scrollTo('platform')} data-testid="hero-cta-secondary"
                className="px-8 py-4 bg-white hover:bg-slate-100 border border-slate-300 text-slate-900 rounded-2xl font-semibold text-lg transition-all">
                See the platform
              </button>
            </div>
            <p className="mt-6 text-slate-500 text-sm" data-testid="hero-trust-line">
              Everything on this page is the live product, not mockups.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={200} className="mt-16 max-w-5xl mx-auto">
            <ShotFrame src={`${SHOT}/overview.webp`} alt="Law Agency 360 client dashboard" data-testid="hero-shot" />
          </AnimatedSection>
        </div>
      </section>

      {/* Value props */}
      <section className="relative py-20 px-6 lg:px-8 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 text-slate-900">Stop building. <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">Start delivering.</span></h2>
            <p className="text-slate-600 text-lg leading-relaxed">Building an in-house client platform costs years and a fortune. Adopt a finished one and put your energy into winning and keeping law-firm clients.</p>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="value-props-grid">
            {valueProps.map((v, i) => {
              const Icon = v.icon;
              return (
                <AnimatedSection key={i} delay={i * 100}>
                  <div className="h-full bg-white border border-slate-200 hover:border-violet-300 hover:shadow-lg rounded-2xl p-7 transition-all hover:-translate-y-1" data-testid={`value-prop-${i}`}>
                    <div className="w-12 h-12 rounded-xl bg-violet-100 border border-violet-200 flex items-center justify-center mb-5">
                      <Icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-slate-900 font-semibold text-lg mb-2">{v.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{v.body}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform tour (screenshot-driven) */}
      <section id="platform" className="relative py-24 px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 border border-violet-200 rounded-full mb-6">
              <LayoutDashboard className="w-4 h-4 text-violet-600" />
              <span className="text-violet-700 text-sm font-medium">A look inside the platform</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-5 text-slate-900">Everything your law-firm clients experience</h2>
            <p className="text-slate-600 text-lg leading-relaxed">From the first onboarding step to monthly reporting, here is exactly what your agency would be delivering under its own roof.</p>
          </AnimatedSection>

          <div className="space-y-24">
            {tour.map((item, i) => {
              const reverse = i % 2 === 1;
              return (
                <AnimatedSection key={i}>
                  <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`} data-testid={`tour-item-${i}`}>
                    <div>
                      <span className="inline-block px-3 py-1 rounded-full bg-violet-100 border border-violet-200 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-5">{item.tag}</span>
                      <h3 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4">{item.title}</h3>
                      <p className="text-slate-600 text-lg leading-relaxed mb-6">{item.body}</p>
                      <ul className="space-y-3">
                        {item.points.map((p, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-5 h-5 rounded-md bg-violet-100 border border-violet-200 flex items-center justify-center mt-0.5">
                              <Check className="w-3 h-3 text-violet-600" />
                            </div>
                            <span className="text-slate-700">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <ShotFrame src={item.img} alt={item.title} />
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Add-on services visual grid */}
      <section className="relative py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 text-slate-900">More services, same dashboard</h2>
            <p className="text-slate-600 text-lg leading-relaxed">Social media, paid ads, AI video, websites, keyword research and even legal translations. Every service your clients buy lives in the same place, with the same approvals and visibility.</p>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="extra-services-grid">
            {extraServices.map((s, i) => (
              <AnimatedSection key={i} delay={(i % 3) * 100}>
                <div className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:border-violet-300 hover:shadow-lg transition-all hover:-translate-y-1" data-testid={`extra-service-${i}`}>
                  <div className="overflow-hidden">
                    <img src={s.img} alt={s.title} loading="lazy" className="w-full block group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                  <div className="px-5 py-4 border-t border-slate-200 text-slate-900 font-semibold">{s.title}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Capability summary */}
      <section className="relative py-20 px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 text-slate-900">A complete client-experience layer</h2>
            <p className="text-slate-600 text-lg leading-relaxed">Everything an agency needs to onboard, manage, report, approve and communicate, all in one system.</p>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="capabilities-grid">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <AnimatedSection key={i} delay={(i % 4) * 80}>
                  <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-4">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-violet-100 border border-violet-200 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-violet-600" />
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{c.label}</span>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works for agencies */}
      <section id="how-it-works" className="relative py-24 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-5 text-slate-900">How agencies roll it out</h2>
            <p className="text-slate-600 text-lg leading-relaxed">From adoption to a fully managed client base, without a single line of code.</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {agencySteps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div className="h-full bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-violet-600 to-indigo-600 mb-4">{step.number}</div>
                  <h3 className="text-slate-900 font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / enquiry */}
      <section id="contact" className="relative py-24 px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 border border-emerald-200 rounded-full mb-6">
              <Rocket className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-700 text-sm font-medium">See it for your agency</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight text-slate-900">Add the platform your clients deserve</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              Tell us a little about your agency and we'll show you exactly how Law Agency 360 would look running your law-firm clients.
              No long contracts and no internal build, just a complete client platform ready to adopt.
            </p>
            <ul className="space-y-4">
              {['A full walkthrough of the live platform', 'How onboarding and reporting work for your clients', 'How to roll it out across your client base'].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-violet-100 border border-violet-200 flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5 text-violet-600" />
                  </div>
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
            <a href="mailto:support@lawagency360.com" className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="contact-support-email">
              <Mail className="w-4 h-4 text-violet-600" /> support@lawagency360.com
            </a>
          </AnimatedSection>
          <AnimatedSection delay={150}>
            <ContactForm />
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing (moved to bottom, retained) */}
      <section id="pricing" className="relative py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 border border-violet-200 rounded-full mb-6">
              <span className="text-violet-700 text-sm font-medium">Client Pricing</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Plans you can place your clients on -
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">simple and transparent.</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
              Monthly plans with no long-term contracts. Cancel anytime. Save 25% when billed yearly. Adopting across your whole client base? <button onClick={() => scrollTo('contact')} className="text-violet-600 hover:text-violet-700 underline underline-offset-2" data-testid="pricing-contact-link">Request a demo</button>.
            </p>
          </AnimatedSection>

          {/* Currency + Billing Switcher */}
          <AnimatedSection delay={100}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="pricing-controls">
              <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm" role="tablist" aria-label="Currency">
                {Object.values(currencies).map((c) => (
                  <button key={c.code} onClick={() => setCurrency(c.code)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${currency === c.code ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30' : 'text-slate-500 hover:text-slate-900'}`}
                    data-testid={`currency-${c.code.toLowerCase()}-btn`} aria-pressed={currency === c.code}>
                    {c.symbol} {c.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm" role="tablist" aria-label="Billing period">
                <button onClick={() => setBilling('monthly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30' : 'text-slate-500 hover:text-slate-900'}`}
                  data-testid="billing-monthly-btn" aria-pressed={billing === 'monthly'}>Monthly</button>
                <button onClick={() => setBilling('yearly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${billing === 'yearly' ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30' : 'text-slate-500 hover:text-slate-900'}`}
                  data-testid="billing-yearly-btn" aria-pressed={billing === 'yearly'}>
                  Yearly
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${billing === 'yearly' ? 'bg-emerald-400 text-emerald-950' : 'bg-emerald-100 text-emerald-700'}`}>Save 25%</span>
                </button>
              </div>
            </div>
          </AnimatedSection>

          {/* Plan cards */}
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch" data-testid="pricing-grid">
            {plans.map((plan, idx) => {
              const price = billing === 'monthly' ? plan.monthly[currency] : plan.yearly[currency];
              const symbol = currencies[currency].symbol;
              return (
                <AnimatedSection key={plan.id} delay={idx * 120} className="h-full">
                  <div data-testid={`plan-card-${plan.id}`}
                    className={`relative h-full flex flex-col rounded-3xl p-8 transition-all duration-300 ${plan.highlighted ? 'bg-violet-50 border border-violet-300 shadow-xl shadow-violet-200/60 lg:scale-[1.03] hover:shadow-violet-300/70 hover:-translate-y-1' : 'bg-white border border-slate-200 hover:border-slate-300 hover:shadow-lg hover:-translate-y-0.5'}`}>
                    {plan.highlighted && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-bold rounded-full shadow-lg shadow-violet-600/40">
                          <Sparkles className="w-3.5 h-3.5" />{plan.badge}
                        </div>
                      </div>
                    )}
                    <div className="relative">
                      <h3 className="text-2xl font-bold text-slate-900">{plan.title}</h3>
                      <p className="text-slate-500 text-sm mt-2">{plan.tagline}</p>
                      <div className="mt-6 flex items-baseline gap-2">
                        <span className="text-5xl font-bold text-slate-900 tracking-tight" data-testid={`plan-${plan.id}-price`}>{symbol}{formatPrice(price)}</span>
                        <span className="text-slate-500 text-sm font-medium">/ {billing === 'monthly' ? 'month' : 'year'}</span>
                      </div>
                      {billing === 'yearly' && (<p className="text-emerald-600 text-xs font-semibold mt-2">Save 25% vs monthly</p>)}
                      {billing === 'monthly' && (<p className="text-slate-500 text-xs mt-2">or {symbol}{formatPrice(plan.yearly[currency])}/year (save 25%)</p>)}
                      <button onClick={() => navigate('/auth')}
                        className={`mt-7 w-full px-6 py-3.5 rounded-2xl font-semibold transition-all flex items-center justify-center gap-2 ${plan.highlighted ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-lg shadow-violet-600/30 hover:shadow-violet-500/50 hover:scale-[1.02]' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-900'}`}
                        data-testid={`plan-${plan.id}-cta`}>
                        Get Started <ArrowRight className="w-4 h-4" />
                      </button>
                      <div className="mt-7 pt-7 border-t border-slate-200">
                        <ul className="space-y-3">
                          {plan.features.map((feat, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-5 h-5 rounded-md bg-violet-100 border border-violet-200 flex items-center justify-center mt-0.5">
                                <Check className="w-3 h-3 text-violet-600" />
                              </div>
                              <span className="text-sm text-slate-700 leading-relaxed">{feat}</span>
                            </li>
                          ))}
                          {plan.excluded.map((feat, i) => (
                            <li key={`x-${i}`} className="flex items-start gap-3 opacity-70">
                              <div className="flex-shrink-0 w-5 h-5 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center mt-0.5">
                                <X className="w-3 h-3 text-slate-400" />
                              </div>
                              <span className="text-sm text-slate-400 leading-relaxed line-through decoration-slate-300">{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">Frequently Asked<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">Questions</span></h2>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm">
              {faqs.map((faq, idx) => (<FAQItem key={idx} question={faq.q} answer={faq.a} />))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="relative bg-gradient-to-br from-violet-600 to-indigo-600 rounded-[2.5rem] p-12 md:p-16 text-center overflow-hidden shadow-2xl shadow-violet-300/50">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-white/10 rounded-full blur-[100px]"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Give your agency a platform<br /><span className="text-violet-100">clients won't want to leave.</span></h2>
                <p className="text-violet-100 text-lg mb-2 max-w-2xl mx-auto">Add a complete technology and client-experience layer to your business, without building it yourself.</p>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button onClick={() => scrollTo('contact')} data-testid="final-cta"
                    className="group px-10 py-5 bg-white text-violet-700 hover:bg-violet-50 rounded-2xl font-semibold text-lg transition-all hover:shadow-2xl hover:shadow-violet-900/20 inline-flex items-center gap-3">
                    Request a demo <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button onClick={() => scrollTo('platform')} data-testid="final-cta-secondary"
                    className="px-10 py-5 bg-white/10 hover:bg-white/20 border border-white/30 text-white rounded-2xl font-semibold text-lg transition-all">
                    Explore the platform
                  </button>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 px-6 lg:px-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BrandLogo size={40} />
                <span className="text-xl font-bold text-slate-900">Law Agency 360</span>
              </div>
              <p className="text-slate-500 text-sm">A complete, ready-built client delivery platform for agencies that serve law firms.</p>
              <a href="mailto:support@lawagency360.com" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="footer-support-email">
                <Mail className="w-4 h-4 text-violet-600" /> support@lawagency360.com
              </a>
            </div>
            <div>
              <h4 className="text-slate-900 font-semibold mb-4">Platform</h4>
              <ul className="space-y-3 text-slate-500 text-sm">
                <li><button onClick={() => scrollTo('platform')} className="hover:text-slate-900 transition-colors">Platform tour</button></li>
                <li><button onClick={() => scrollTo('how-it-works')} className="hover:text-slate-900 transition-colors">How it works</button></li>
                <li><button onClick={() => scrollTo('pricing')} className="hover:text-slate-900 transition-colors">Pricing</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-slate-900 font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-slate-500 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-slate-900 transition-colors" data-testid="footer-about-link">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-slate-900 transition-colors">Blog</button></li>
                <li><button onClick={() => scrollTo('contact')} className="hover:text-slate-900 transition-colors" data-testid="footer-contact-link">Request a demo</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-slate-900 font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-slate-500 text-sm">
                <li><button onClick={() => navigate('/privacy-policy')} className="hover:text-slate-900 transition-colors" data-testid="footer-privacy-link">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-slate-900 transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</button></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-slate-500 text-sm">
            <div className="text-center sm:text-left">&copy; 2026 Law Agency 360. All rights reserved.</div>
            <div className="flex items-center justify-center gap-3">
              <a href="https://www.linkedin.com/company/law-agency-360/" target="_blank" rel="noopener noreferrer" aria-label="Law Agency 360 LinkedIn" title="Law Agency 360 LinkedIn"
                className="w-9 h-9 inline-flex items-center justify-center rounded-xl bg-slate-100 border border-slate-200 text-slate-500 hover:text-violet-600 hover:bg-violet-50 hover:border-violet-200 transition-all" data-testid="footer-linkedin-link">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                  <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.72C24 .77 23.2 0 22.22 0z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
